import { useHead } from "@vueuse/head";

export default {
  name: "VideoView",
  setup() {
    useHead({
      title: "產品影片展示｜安裕和國際企業社",
      meta: [
        {
          name: "description",
          content:
            "安裕和國際企業社產品示範影片、使用教學、臨床操作影片展示，快速了解醫療設備特性與使用方式。",
        },
        {
          name: "keywords",
          content:
            "動物醫療影片, 產品示範, 使用教學, 動物醫療設備, 醫療器材影片",
        },

        // Open Graph
        { property: "og:title", content: "安裕和國際企業社｜產品影片展示" },
        {
          property: "og:description",
          content:
            "觀看動物醫療用品的使用示範、產品介紹與臨床教學影片，更快速理解產品特性。",
        },
        {
          property: "og:image",
          content: "https://www.annyoohoh.com.tw/img/AYH.f5146a33.png",
        },
        { property: "og:type", content: "website" },
        {
          property: "og:url",
          content: "https://www.annyoohoh.com.tw/video",
        },
      ],

      link: [
        {
          rel: "canonical",
          href: "https://www.annyoohoh.com.tw/video",
        },
      ],
    });
  },
  data() {
    return {
      // ✅ 影片資料
      videos: [
        {
          id: 1,
          title: "安裕和 親水塗層鼻餵管",
          youtubeId: "YmC8Fl0ydBs", // ✅ 取自 https://youtu.be/YmC8Fl0ydBs
          description: "柔軟低刺激，附無菌水立即用，提升操作效率，也安心。",
        },
        {
          id: 2,
          title: "安裕和 親水塗層貓用導尿管",
          youtubeId: "9qXuB-VsZmY", // ✅ 取自 https://youtu.be/9qXuB-VsZmY
          description: "柔軟潤滑、減刺激、留置更安心。",
        },
      ],

      activeCategory: "all", // ✅ 當前分類
      selectedVideo: null, // ✅ 選取的影片
      currentPage: 1, // ✅ 當前頁數
      pageSize: 6, // ✅ 每頁顯示數量
      isLoading: false, // ✅ 載入狀態
    };
  },

  computed: {
    // ✅ 過濾後影片
    filteredVideos() {
      if (this.activeCategory === "all") return this.videos;
      return this.videos.filter(
        (video) => video.category === this.activeCategory
      );
    },

    // ✅ 分頁後影片
    paginatedVideos() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredVideos.slice(start, end);
    },

    // ✅ 總頁數
    totalPages() {
      return Math.ceil(this.filteredVideos.length / this.pageSize);
    },

    // ✅ 動態顯示頁碼
    displayedPages() {
      const pages = [];
      const maxPageButtons = 5;

      if (this.totalPages <= maxPageButtons) {
        for (let i = 1; i <= this.totalPages; i++) pages.push(i);
      } else {
        let start = Math.max(
          1,
          this.currentPage - Math.floor(maxPageButtons / 2)
        );
        let end = start + maxPageButtons - 1;

        if (end > this.totalPages) {
          end = this.totalPages;
          start = end - maxPageButtons + 1;
        }

        for (let i = start; i <= end; i++) pages.push(i);
      }

      return pages;
    },

    showLoadMoreButton() {
      return false;
    },
  },

  watch: {
    // ✅ 當分類變更，重置頁面
    activeCategory() {
      this.currentPage = 1;
    },
  },

  methods: {
    // ✅ 格式化日期
    formatDate(dateString) {
      if (!dateString) return "";
      const opt = { year: "numeric", month: "long", day: "numeric" };
      return new Date(dateString).toLocaleDateString("zh-TW", opt);
    },

    // ✅ 影片分類翻譯
    translateCategory(category) {
      const categoryMap = {
        all: "全部",
        tutorial: "使用教學",
        demo: "產品示範",
        review: "評測影片",
        new: "新品介紹",
      };
      return categoryMap[category] || category;
    },

    // ✅ 分類切換
    filterByCategory(category) {
      this.activeCategory = category;
      this.currentPage = 1;
      this.scrollToVideoTop();
    },

    // ✅ 分頁切換
    changePage(pageNum) {
      this.currentPage = pageNum;
      this.scrollToVideoTop(); // ✅ 捲動回最頂
    },

    // ✅ 上一頁
    prevPage() {
      if (this.currentPage > 1) {
        this.changePage(this.currentPage - 1);
      }
    },

    // ✅ 下一頁
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.changePage(this.currentPage + 1);
      }
    },

    // ✅ 載入更多（保留相容性）
    loadMore() {
      if (this.currentPage < this.totalPages) this.nextPage();
    },

    // ✅ 捲回影片最上面（關鍵）
    scrollToVideoTop() {
      const section = document.querySelector(".video-section");
      if (section) {
        const offsetTop =
          section.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: offsetTop - 120, // 如果有固定 navbar 就微調
          behavior: "smooth",
        });
      }
    },

    getYoutubeEmbedUrl(videoId) {
      if (!videoId) return "";
      // 使用 youtube-nocookie 可避免錯誤 153
      return `https://www.youtube-nocookie.com/embed/${videoId}`;
    },

    // ✅ 打開影片詳情
    openVideoDetail(video) {
      this.selectedVideo = video;
      document.body.style.overflow = "hidden";

      if (window.innerWidth < 768) {
        setTimeout(() => {
          const modal = document.querySelector(".video-detail-container");
          if (modal) modal.scrollTop = 0;
        }, 100);
      }
    },

    // ✅ 關閉影片詳情
    closeVideoDetail() {
      this.selectedVideo = null;
      document.body.style.overflow = "";
    },

    // ✅ 推薦影片
    getRelatedVideos() {
      if (!this.selectedVideo) return [];

      let related = this.videos
        .filter(
          (v) =>
            v.id !== this.selectedVideo.id &&
            v.category === this.selectedVideo.category
        )
        .slice(0, 3);

      if (related.length < 3) {
        const others = this.videos
          .filter(
            (v) =>
              v.id !== this.selectedVideo.id &&
              (!v.category || v.category !== this.selectedVideo.category)
          )
          .slice(0, 3 - related.length);

        related = [...related, ...others];
      }
      return related;
    },
  },

  mounted() {
    console.log("Video Page Component mounted");

    // ✅ 影片頁面通常內容相對靜態，直接觸發預渲染事件
    this.$nextTick(() => {
      if (typeof document !== "undefined") {
        console.log("教學影片頁面載入完成，觸發預渲染事件");
        document.dispatchEvent(new Event("render-event"));
      }
    });
  },
};
